/**
 * Pure-math helpers for the constellation-overlay layer.
 *
 * Cesium-free so the conversion math + JSON-shape validation are
 * covered by Vitest directly. The Cesium-using `ConstellationLayer`
 * imports these helpers + the JSON data, and applies them per-frame.
 *
 * Coordinate convention:
 *   - `ra`   right ascension, degrees in [0, 360)
 *   - `dec`  declination,     degrees in [-90, +90]
 *   - Both in the J2000 inertial frame (ICRF for the BSC5 skybox
 *     cubemap; the small TEME/ICRF disagreement at the arcsecond
 *     level is invisible for visual line drawing).
 *
 * Each star's unit vector is computed ONCE at attach time; the
 * per-frame work is just multiplying these constants by the current
 * inertial→ECEF rotation matrix and scaling out to the celestial-
 * sphere radius.
 */

export interface ConstellationStarSpec {
  /** Common-name reference for debugging / future tooltip use. */
  name?: string;
  /** Right ascension, degrees in [0, 360). */
  ra: number;
  /** Declination, degrees in [-90, +90]. */
  dec: number;
}

export interface ConstellationLabelPosition {
  ra: number;
  dec: number;
}

export interface ConstellationSpec {
  /** Full name displayed as the label (e.g. "Orion"). */
  name: string;
  /** Optional 3-letter IAU abbreviation (e.g. "Ori"). */
  abbrev?: string;
  /** Stars referenced by index from the `lines` array. */
  stars: ConstellationStarSpec[];
  /** Pairs of indices into `stars` representing line segments. */
  lines: ReadonlyArray<readonly [number, number]>;
  /** Where the constellation name should render. */
  label: ConstellationLabelPosition;
}

export interface ConstellationsPayload {
  constellations: ConstellationSpec[];
}

/**
 * Convert an RA/Dec pair (degrees, J2000) to a unit vector in the
 * inertial cartesian frame: x = cos(dec) cos(ra),
 *                          y = cos(dec) sin(ra),
 *                          z = sin(dec).
 *
 * Returns the standard `[x, y, z]` tuple in [-1, 1]. Defensive
 * against NaN inputs — falls back to (0, 0, 0) so callers can
 * skip the entry rather than crash.
 */
export function raDecToInertialUnitVector(raDeg: number, decDeg: number): [number, number, number] {
  if (!Number.isFinite(raDeg) || !Number.isFinite(decDeg)) {
    return [0, 0, 0];
  }
  const ra = (raDeg * Math.PI) / 180;
  const dec = (decDeg * Math.PI) / 180;
  const cosDec = Math.cos(dec);
  return [cosDec * Math.cos(ra), cosDec * Math.sin(ra), Math.sin(dec)];
}

/**
 * Validate the shape of a `ConstellationsPayload` parsed from JSON.
 * Returns the typed payload on success, or `null` when any
 * structural invariant is violated. Defensive at the boundary so
 * a malformed `constellations.json` doesn't crash the runtime.
 *
 * Invariants checked:
 *   - `constellations` is an array
 *   - each entry has a non-empty `name`, an array of `stars`, an
 *     array of `lines`, and a `label`
 *   - every star has finite numeric ra/dec
 *   - ra ∈ [0, 360), dec ∈ [-90, 90]
 *   - every line-pair indexes a real star
 *   - label coords are finite
 */
export function validateConstellationsPayload(raw: unknown): ConstellationsPayload | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const list = obj.constellations;
  if (!Array.isArray(list)) return null;

  const out: ConstellationSpec[] = [];
  for (const entry of list) {
    if (typeof entry !== 'object' || entry === null) return null;
    const e = entry as Record<string, unknown>;
    if (typeof e.name !== 'string' || e.name.length === 0) return null;
    if (!Array.isArray(e.stars) || e.stars.length === 0) return null;
    if (!Array.isArray(e.lines)) return null;
    if (typeof e.label !== 'object' || e.label === null) return null;

    const stars: ConstellationStarSpec[] = [];
    for (const s of e.stars) {
      if (typeof s !== 'object' || s === null) return null;
      const star = s as Record<string, unknown>;
      const ra = star.ra;
      const dec = star.dec;
      if (typeof ra !== 'number' || !Number.isFinite(ra) || ra < 0 || ra >= 360) return null;
      if (typeof dec !== 'number' || !Number.isFinite(dec) || dec < -90 || dec > 90) return null;
      const spec: ConstellationStarSpec = { ra, dec };
      if (typeof star.name === 'string') spec.name = star.name;
      stars.push(spec);
    }

    const lines: Array<readonly [number, number]> = [];
    for (const pair of e.lines) {
      if (!Array.isArray(pair) || pair.length !== 2) return null;
      const a = pair[0];
      const b = pair[1];
      if (typeof a !== 'number' || typeof b !== 'number') return null;
      if (!Number.isInteger(a) || !Number.isInteger(b)) return null;
      if (a < 0 || a >= stars.length) return null;
      if (b < 0 || b >= stars.length) return null;
      lines.push([a, b] as const);
    }

    const labelObj = e.label as Record<string, unknown>;
    const labelRa = labelObj.ra;
    const labelDec = labelObj.dec;
    if (typeof labelRa !== 'number' || !Number.isFinite(labelRa) || labelRa < 0 || labelRa >= 360) return null;
    if (typeof labelDec !== 'number' || !Number.isFinite(labelDec) || labelDec < -90 || labelDec > 90) return null;

    const spec: ConstellationSpec = {
      name: e.name,
      stars,
      lines,
      label: { ra: labelRa, dec: labelDec },
    };
    if (typeof e.abbrev === 'string') spec.abbrev = e.abbrev;
    out.push(spec);
  }

  return { constellations: out };
}

/**
 * Distance (meters) from Earth's center at which the constellation
 * lines + labels are rendered. Far enough that parallax from any
 * realistic orbital camera position is invisible (sub-arcsecond at
 * LEO, <3° at the Wide zoom-out), close enough to sit comfortably
 * inside Cesium's logarithmic depth-buffer's effective range so the
 * primitives actually render. 1 Gm is ~2.6× past the Moon and ~28×
 * past GEO — well clear of any satellite — but well inside the
 * frustum cliff that hides primitives much further out.
 *
 * A prior 50-Gm value (33× larger) made the polylines invisible at
 * runtime even though they were positioned correctly — confirmed by
 * Robert reporting nothing visible after toggling the overlay.
 */
export const CELESTIAL_SPHERE_RADIUS_M = 1_000_000_000;
